import React, { useState, useEffect, useRef } from 'react';
import { Search, Calendar, Star, ExternalLink, Sparkles, CheckCircle, Copy, ChevronDown, ChevronUp } from 'lucide-react';

const ReviewsDashboard = ({ api, onNotification }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [generatedResponses, setGeneratedResponses] = useState({});
  const [, setUpdatingStatus] = useState(null); // eslint-disable-line no-unused-vars
  const [copiedId, setCopiedId] = useState(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);
  
  const [filters, setFilters] = useState({
    status: 'pending',
    rating: 'all',
    search: '',
    dateRange: 'all',
    startDate: '',
    endDate: ''
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0
  });

  const [sortBy, setSortBy] = useState('date-desc');

  // Load reviews when filters change
useEffect(() => {
  loadReviews(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [filters.status, filters.rating, filters.dateRange, filters.startDate, filters.endDate, sortBy]);

  // Handle dropdown clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadReviews = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.pageSize.toString()
      });

      // Add sorting
      if (sortBy === 'date-desc') {
          params.append('sortBy', 'publishedDate');
          params.append('sortOrder', 'desc');
        } else if (sortBy === 'date-asc') {
          params.append('sortBy', 'publishedDate');
          params.append('sortOrder', 'asc');
        } else if (sortBy === 'rating-high') {
          params.append('sortBy', 'rating');
          params.append('sortOrder', 'desc');
        } else if (sortBy === 'rating-low') {
          params.append('sortBy', 'rating');
          params.append('sortOrder', 'asc');
        }
      // Add filters to query - backend will handle filtering
      if (filters.status === 'pending') params.append('responded', 'false');
      else if (filters.status === 'responded') params.append('responded', 'true');
      else if (filters.status === 'no_response') params.append('responded', 'no_response_needed');
 
      if (filters.rating !== 'all') params.append('rating', filters.rating);
      if (filters.dateRange !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        if (filters.dateRange === 'today') {
          params.append('startDate', today.toISOString());
        } else if (filters.dateRange === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          params.append('startDate', weekAgo.toISOString());
        } else if (filters.dateRange === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          params.append('startDate', monthAgo.toISOString());
        } else if (filters.dateRange === 'custom') {
          if (filters.startDate) params.append('startDate', filters.startDate);
          if (filters.endDate) {
            const endDate = new Date(filters.endDate);
            endDate.setHours(23, 59, 59, 999);
            params.append('endDate', endDate.toISOString());
          }
        }
      }
      
      const data = await api.request(`/reviews?${params.toString()}`);
  
      setReviews(Array.isArray(data.reviews) ? data.reviews : []);
      
      if (data.pagination) {
        setPagination({
          currentPage: data.pagination.page,
          pageSize: data.pagination.limit,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages
        });
      }
    } catch (error) {
      console.error('Failed to load reviews:', error);
      if (onNotification) {
        onNotification({ message: 'Failed to load reviews', type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateResponse = async (reviewId) => {
    setGeneratingFor(reviewId);
    try {
      const result = await api.generateReviewResponse(reviewId);
      setGeneratedResponses({ ...generatedResponses, [reviewId]: result.generatedResponse });
    } catch (error) {
      console.error('Failed to generate:', error);
    } finally {
      setGeneratingFor(null);
    }
  };

  const handleCopyResponse = (reviewId, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(reviewId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUpdateStatus = async (reviewId, newStatus) => {
    setUpdatingStatus(reviewId);
    setStatusDropdownOpen(null);
    try {
      await api.updateReviewStatus(reviewId, newStatus);
      await loadReviews(pagination.currentPage);
      setExpandedRow(null);
      setGeneratedResponses({ ...generatedResponses, [reviewId]: null });
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch(status) {
      case 'RESPONDED':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'NO_RESPONSE_NEEDED':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-purple-100 text-purple-700 border-purple-200';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'RESPONDED':
        return 'Responded';
      case 'NO_RESPONSE_NEEDED':
        return 'No Response Needed';
      default:
        return 'Pending';
    }
  };

  const renderStars = (rating) => (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className={`w-3 h-3 ${i < rating ? 'fill-purple-500 text-purple-500' : 'fill-gray-200 text-gray-200'}`} />
      ))}
    </div>
  );

  return (

    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="px-6 py-6">
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">{pagination.total}</div>
              <div className="text-xs text-gray-600 mt-1">Total Reviews</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-2xl font-bold text-purple-900">
                {filters.status === 'pending' ? pagination.total : '—'}
              </div>
              <div className="text-xs text-purple-700 mt-1">Awaiting Response</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-900">
                {filters.status === 'responded' ? pagination.total : '—'}
              </div>
              <div className="text-xs text-green-700 mt-1">Responded</div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">
                {filters.status === 'no_response' ? pagination.total : '—'}
              </div>
              <div className="text-xs text-gray-700 mt-1">No Response Needed</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border-b px-6 py-4">
        <div className="grid grid-cols-4 gap-3 mb-3">
          <div className="relative col-span-2">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input 
              type="text" 
              placeholder="Search reviews..." 
              value={filters.search} 
              onChange={(e) => setFilters({ ...filters, search: e.target.value })} 
              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" 
            />
          </div>
          <select 
            value={filters.status} 
            onChange={(e) => setFilters({ ...filters, status: e.target.value })} 
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
          >
            <option value="pending">Pending</option>
            <option value="responded">Responded</option>
            <option value="no_response">No Response Needed</option>
            <option value="all">All Reviews</option>
          </select>
          <select 
            value={filters.rating} 
            onChange={(e) => setFilters({ ...filters, rating: e.target.value })} 
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
          >
            <option value="all">All ratings</option>
            <option value="5">5 stars</option>
            <option value="4">4 stars</option>
            <option value="3">3 stars</option>
            <option value="2">2 stars</option>
            <option value="1">1 star</option>
          </select>

        </div>

        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-gray-400" />
          <select 
            value={filters.dateRange} 
            onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })} 
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="custom">Custom range</option>
          </select>
          {filters.dateRange === 'custom' && (
            <>
              <input 
                type="date" 
                value={filters.startDate} 
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} 
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" 
              />
              <span className="text-sm text-gray-500">to</span>
              <input 
                type="date" 
                value={filters.endDate} 
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} 
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" 
              />
            </>
          )}
          <div className="flex-1" />
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)} 
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
          >
            <option value="date-desc">Newest first</option>
            <option value="date-asc">Oldest first</option>

          </select>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider w-12"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Author</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Rating</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">Loading...</td></tr>
              ) : reviews.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">No reviews found</td></tr>
              ) : (
                reviews.map((review) => (
                  <React.Fragment key={review.id}>
                    <tr 
                      className={`hover:bg-gray-50 cursor-pointer ${expandedRow === review.id ? 'bg-gray-50' : ''}`} 
                      onClick={() => setExpandedRow(expandedRow === review.id ? null : review.id)}
                    >
                      <td className="px-6 py-4">
                        {expandedRow === review.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{review.authorName}</div>
                      </td>
                      <td className="px-6 py-4">{renderStars(review.rating)}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                          {review.platform === 'TRUSTPILOT' ? 'Trustpilot' : 'Google'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(review.publishedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="relative" ref={statusDropdownOpen === review.id ? dropdownRef : null}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setStatusDropdownOpen(statusDropdownOpen === review.id ? null : review.id);
                            }}
                            className={`px-2 py-1 text-xs font-medium rounded border flex items-center gap-1.5 hover:opacity-80 transition-opacity ${getStatusBadgeStyle(review.replyStatus)}`}
                          >
                            {getStatusLabel(review.replyStatus)}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          
                          {statusDropdownOpen === review.id && (
                            <div className="absolute top-full mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[180px]">
                              <button
                                onClick={() => handleUpdateStatus(review.id, 'NEEDS_RESPONSE')}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 flex items-center justify-between"
                              >
                                <span>Pending</span>
                                {review.replyStatus === 'NEEDS_RESPONSE' && <CheckCircle className="w-3.5 h-3.5 text-purple-600" />}
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(review.id, 'RESPONDED')}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 flex items-center justify-between"
                              >
                                <span>Responded</span>
                                {review.replyStatus === 'RESPONDED' && <CheckCircle className="w-3.5 h-3.5 text-green-600" />}
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(review.id, 'NO_RESPONSE_NEEDED')}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                              >
                                <span>No Response Needed</span>
                                {review.replyStatus === 'NO_RESPONSE_NEEDED' && <CheckCircle className="w-3.5 h-3.5 text-gray-600" />}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            window.open(review.externalUrl, '_blank'); 
                          }} 
                          className="px-3 py-1.5 text-xs font-medium text-purple-600 border border-purple-300 rounded hover:bg-purple-50 transition-colors flex items-center gap-1.5 ml-auto"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Respond
                        </button>
                      </td>
                    </tr>
                    {expandedRow === review.id && (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-gray-700 leading-relaxed">{review.reviewText}</p>
                            </div>

                            {review.replyStatus === 'RESPONDED' && review.responseText && (
                              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-xs font-medium text-green-900 mb-2">Your response</p>
                                <p className="text-sm text-green-800">{review.responseText}</p>
                              </div>
                            )}

                            {review.replyStatus === 'NEEDS_RESPONSE' && generatedResponses[review.id] && (
                              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-medium text-purple-900">AI suggested response</p>
                                  <button onClick={() => handleCopyResponse(review.id, generatedResponses[review.id])} className="p-1 text-purple-600 hover:text-purple-700">
                                    {copiedId === review.id ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                  </button>
                                </div>
                                <p className="text-sm text-purple-800">{generatedResponses[review.id]}</p>
                              </div>
                            )}

                            <div className="flex gap-2">
                              {review.replyStatus === 'NEEDS_RESPONSE' && !generatedResponses[review.id] && (
                                <button 
                                  onClick={() => handleGenerateResponse(review.id)} 
                                  disabled={generatingFor === review.id} 
                                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                  {generatingFor === review.id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="w-3.5 h-3.5" />
                                      Generate response
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
          
          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {pagination.total === 0 ? (
                'No reviews found'
              ) : (
                `Showing ${((pagination.currentPage - 1) * pagination.pageSize) + 1} to ${Math.min(pagination.currentPage * pagination.pageSize, pagination.total)} of ${pagination.total} reviews`
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => loadReviews(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm">
                Page {pagination.currentPage} of {pagination.totalPages || 1}
              </span>
              <button
                onClick={() => loadReviews(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsDashboard;